import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config/api';

const DrawGame = () => {
  const canvasRef = useRef(null);
  const desktopCanvasRef = useRef(null);
  const lastActiveCanvasRef = useRef(null);
  const drawingActiveRef = useRef(false);
  const lastPointRef = useRef(null);
  const drawnDistanceRef = useRef(0);
  const hasDrawingRef = useRef(false);
  const [players, setPlayers] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [selectedSport, setSelectedSport] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [loading, setLoading] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState(null);
  const [gameStartTime, setGameStartTime] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [hasDrawing, setHasDrawing] = useState(false);
  const [drawingError, setDrawingError] = useState('');

  const getVisibleCanvas = useCallback(() => {
    const isDesktopLayout = typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(min-width: 1024px)').matches;

    return (isDesktopLayout ? desktopCanvasRef.current : canvasRef.current)
      || canvasRef.current
      || desktopCanvasRef.current;
  }, []);

  const sports = [
    { value: 'all', label: 'All Sports' },
    { value: 'football', label: 'Football' },
    { value: 'cricket', label: 'Cricket' },
    { value: 'tennis', label: 'Tennis' },
    { value: 'basketball', label: 'Basketball' }
  ];

  const difficulties = [
    { value: 'all', label: 'All Levels' },
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' }
  ];

  const fetchPlayers = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_BASE_URL}/api/draw-game/players?`;
      if (selectedSport !== 'all') url += `sport=${selectedSport}&`;
      if (selectedDifficulty !== 'all') url += `difficulty=${selectedDifficulty}&`;

      const response = await fetch(url);
      if (response.ok) {
        const data = await response.json();
        setPlayers(data);
      }
    } catch (error) {
      console.error('Error fetching players:', error);
    }
    setLoading(false);
  }, [selectedSport, selectedDifficulty]);

  useEffect(() => {
    fetchPlayers();
  }, [fetchPlayers]);

  const selectPlayer = (player) => {
    drawingActiveRef.current = false;
    lastActiveCanvasRef.current = null;
    lastPointRef.current = null;
    drawnDistanceRef.current = 0;
    hasDrawingRef.current = false;
    setHasDrawing(false);
    setDrawingError('');
    setCurrentPlayer(player);
    setShowResult(false);
    setResult(null);
    setGameStartTime(Date.now());
    setShowLeaderboard(false);
    fetchLeaderboard(player._id);
  };

  const fetchLeaderboard = async (playerId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/draw-game/leaderboard/${playerId}`);
      if (response.ok) {
        const data = await response.json();
        setLeaderboard(data);
      }
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    }
  };

  const initializeCanvas = useCallback((targetCanvas) => {
    const canvas = targetCanvas || getVisibleCanvas();
    if (!canvas) return;

    const container = canvas.parentElement;
    const containerWidth = container.clientWidth;
    const size = Math.min(containerWidth, 500);
    
    canvas.width = size;
    canvas.height = size;

    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = 3;
    ctx.strokeStyle = '#000000';
    lastActiveCanvasRef.current = canvas;
  }, [getVisibleCanvas]);

  useLayoutEffect(() => {
    if (currentPlayer) {
      initializeCanvas(getVisibleCanvas());
    }
  }, [currentPlayer, getVisibleCanvas, initializeCanvas]);

  useEffect(() => {
    if (!currentPlayer) return;

    const handleResize = () => {
      const sourceCanvas = lastActiveCanvasRef.current || getVisibleCanvas();
      const targetCanvas = getVisibleCanvas();
      if (sourceCanvas && targetCanvas) {
        const snapshot = document.createElement('canvas');
        snapshot.width = sourceCanvas.width;
        snapshot.height = sourceCanvas.height;
        snapshot.getContext('2d').drawImage(sourceCanvas, 0, 0);
        initializeCanvas(targetCanvas);
        const resizedContext = targetCanvas.getContext('2d');
        resizedContext.drawImage(snapshot, 0, 0, snapshot.width, snapshot.height, 0, 0, targetCanvas.width, targetCanvas.height);
      }
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [currentPlayer, getVisibleCanvas, initializeCanvas]);

  const getCoordinates = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const canvas = e.currentTarget || canvasRef.current;
    if (!canvas) return;
    lastActiveCanvasRef.current = canvas;

    if (canvas.setPointerCapture && e.pointerId !== undefined) {
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch (error) {
        // Pointer capture is best-effort on older mobile browsers.
      }
    }

    drawingActiveRef.current = true;
    const coords = getCoordinates(e, canvas);
    lastPointRef.current = coords;
    const ctx = canvas.getContext('2d');

    // Give taps immediate visual feedback without treating a single dot as a full drawing.
    ctx.beginPath();
    ctx.arc(coords.x, coords.y, Math.max(ctx.lineWidth / 2, 1.5), 0, Math.PI * 2);
    ctx.fillStyle = ctx.strokeStyle;
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    e.preventDefault();
    if (!drawingActiveRef.current) return;

    const canvas = e.currentTarget || canvasRef.current;
    if (!canvas) return;

    const coords = getCoordinates(e, canvas);
    const previousPoint = lastPointRef.current;
    const ctx = canvas.getContext('2d');

    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();

    if (previousPoint) {
      drawnDistanceRef.current += Math.hypot(coords.x - previousPoint.x, coords.y - previousPoint.y);
      if (!hasDrawingRef.current && drawnDistanceRef.current >= 12) {
        hasDrawingRef.current = true;
        setHasDrawing(true);
        setDrawingError('');
      }
    }
    lastPointRef.current = coords;
  };

  const stopDrawing = (e) => {
    if (e) {
      e.preventDefault();
      const canvas = e.currentTarget || canvasRef.current;
      if (canvas?.hasPointerCapture?.(e.pointerId)) {
        canvas.releasePointerCapture(e.pointerId);
      }
    }
    drawingActiveRef.current = false;
    lastPointRef.current = null;
  };

  const clearCanvas = () => {
    const canvas = lastActiveCanvasRef.current || getVisibleCanvas();
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    drawingActiveRef.current = false;
    lastPointRef.current = null;
    drawnDistanceRef.current = 0;
    hasDrawingRef.current = false;
    setHasDrawing(false);
    setDrawingError('');
    setShowResult(false);
    setResult(null);
  };

  const calculateSimilarity = () => {
    const canvas = lastActiveCanvasRef.current || getVisibleCanvas();
    const ctx = canvas.getContext('2d');
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;

    let drawnPixels = 0;
    let totalPixels = data.length / 4;

    for (let i = 0; i < data.length; i += 4) {
      if (data[i] !== 255 || data[i + 1] !== 255 || data[i + 2] !== 255) {
        drawnPixels++;
      }
    }

    if (drawnPixels === 0) return 0;

    const coverage = drawnPixels / totalPixels;
    let similarity = Math.min(coverage * 200, 100);
    
    const randomFactor = currentPlayer.difficulty === 'easy' ? 0.8 : 
                        currentPlayer.difficulty === 'medium' ? 0.6 : 0.4;
    
    similarity = Math.max(1, similarity + (Math.random() - 0.5) * 40 * randomFactor);
    
    return Math.round(Math.min(similarity, 100));
  };

  const submitDrawing = async () => {
    if (!currentPlayer) return;

    const canvas = lastActiveCanvasRef.current || getVisibleCanvas();
    if (!canvas || !hasDrawingRef.current || drawnDistanceRef.current < 12) {
      setDrawingError('Please draw something before submitting.');
      return;
    }

    setDrawingError('');
    setLoading(true);
    const userDrawing = canvas.toDataURL('image/png');
    const similarityScore = calculateSimilarity();
    const completionTime = gameStartTime ? Date.now() - gameStartTime : 0;

    try {
      const response = await fetch(`${API_BASE_URL}/api/draw-game/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          playerId: currentPlayer._id,
          playerName: currentPlayer.name,
          userDrawing,
          similarityScore,
          completionTime
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unable to submit drawing');
      }

      const data = await response.json();
      setResult(data.result);
      setShowResult(true);
      fetchLeaderboard(currentPlayer._id);
    } catch (error) {
      console.error('Error submitting drawing:', error);
      setDrawingError('Could not submit your drawing. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'easy': return 'bg-green-100 text-green-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'hard': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    return `${minutes}:${(seconds % 60).toString().padStart(2, '0')}`;
  };

  const activatePlayerCard = (player) => {
    selectPlayer(player);
  };

  const handlePlayerCardKeyDown = (event, player) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      selectPlayer(player);
    }
  };

  if (currentPlayer) {
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header - Fully Responsive */}
        <div className="bg-white shadow-sm border-b sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4">
              <button
                onClick={() => setCurrentPlayer(null)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 text-sm sm:text-base transition-colors"
              >
                <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium sm:hidden">Back</span>
                <span className="font-medium hidden sm:inline">Back to Players</span>
              </button>
              
              <div className="text-center flex-1 w-full sm:w-auto order-first sm:order-none">
                <h1 className="text-base sm:text-lg md:text-xl font-bold text-gray-900 truncate px-2">
                  Draw: {currentPlayer.name}
                </h1>
                <p className="text-xs sm:text-sm text-gray-500 truncate">
                  {currentPlayer.sport} • {currentPlayer.team}
                </p>
              </div>

              <span className={`px-2.5 sm:px-3 py-1 rounded-full text-xs font-medium ${getDifficultyColor(currentPlayer.difficulty)} whitespace-nowrap`}>
                {currentPlayer.difficulty.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-4 md:py-6">
          
          {/* MOBILE/TABLET: Side-by-side */}
          <div className="lg:hidden">
            <div className="grid grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
              <div className="bg-white rounded-lg shadow p-3 sm:p-4">
                <h3 className="text-xs sm:text-sm font-semibold mb-2 sm:mb-3 flex items-center gap-1">
                  <span>📷</span>
                  <span className="truncate">Reference</span>
                </h3>
                <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                  <img
                    src={currentPlayer.image}
                    alt={currentPlayer.name}
                    className="w-full h-full object-contain pointer-events-none"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="w-full h-full flex items-center justify-center text-gray-400 bg-gray-50" style={{display: 'none'}}>
                    <span className="text-xs">No Image</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-3 sm:p-4">
                <div className="flex justify-between items-center mb-2 sm:mb-3">
                  <h3 className="text-xs sm:text-sm font-semibold flex items-center gap-1">
                    <span>🎨</span>
                    <span>Draw</span>
                  </h3>
                  <button onClick={clearCanvas} aria-label="Clear drawing" className="text-gray-500 hover:text-gray-700 px-2 py-1 text-xs border border-gray-300 rounded">
                    🗑️
                  </button>
                </div>
                <div className="relative flex w-full items-center justify-center border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white canvas-container">
                  <canvas
                    ref={canvasRef}
                    className="drawing-canvas block w-full h-auto cursor-crosshair touch-none select-none"
                    style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }}
                    onPointerDown={startDrawing}
                    onPointerMove={draw}
                    onPointerUp={stopDrawing}
                    onPointerCancel={stopDrawing}
                    onLostPointerCapture={stopDrawing}
                  />
                </div>
              </div>
            </div>

            <button onClick={submitDrawing} disabled={loading || showResult} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white px-4 py-2.5 sm:py-3 rounded-lg text-sm sm:text-base font-semibold shadow-lg disabled:shadow-none mb-2">
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Analyzing...
                </span>
              ) : showResult ? '✓ Drawing Submitted' : '✨ Submit Drawing'}
            </button>
            <div className={`min-h-[20px] mb-2 sm:mb-3 text-center text-xs font-medium ${drawingError ? 'text-red-600' : 'text-gray-500'}`} role="status" aria-live="polite">
              {drawingError || (!hasDrawing ? 'Draw on the canvas before submitting.' : 'Your drawing is ready to submit.')}
            </div>

            {/* Result & Leaderboard sections remain the same as in original file */}
            <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-3 sm:mb-4">
              <h3 className="text-sm sm:text-base font-semibold mb-3">📊 Result</h3>
              {!showResult ? (
                <div className="text-center py-6 text-gray-500">
                  <svg className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                  </svg>
                  <p className="text-xs sm:text-sm px-4">Complete your drawing and submit!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 sm:p-6">
                    <div className="text-4xl sm:text-5xl font-bold text-blue-600 mb-2">{result.score}%</div>
                    <p className="text-sm text-gray-600 font-medium">Similarity Score</p>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="text-xs font-semibold text-gray-700 mb-1">Rank</div>
                      <div className="text-lg text-blue-600 font-bold">#{result.rank}</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
                      <div className="text-xs font-semibold text-gray-700 mb-1">Time</div>
                      <div className="text-lg text-green-600 font-bold">{formatTime(result.completionTime)}</div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <h4 className="text-xs font-semibold mb-2 text-gray-700">Score Categories</h4>
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span>Draw (90-100%)</span>
                        <span className={result.score >= 90 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Good (70-89%)</span>
                        <span className={result.score >= 70 && result.score < 90 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Average (50-69%)</span>
                        <span className={result.score >= 50 && result.score < 70 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Poor (0-49%)</span>
                        <span className={result.score < 50 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { setGameStartTime(Date.now()); clearCanvas(); }} className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold">
                    🔄 Try Again
                  </button>
                </div>
              )}
            </div>

            {leaderboard.length > 0 && (
              <div className="bg-white rounded-lg shadow p-4">
                <button onClick={() => setShowLeaderboard(!showLeaderboard)} className="w-full bg-gradient-to-r from-blue-50 to-purple-50 text-gray-700 px-4 py-2 rounded-lg text-sm font-semibold border border-blue-200">
                  🏆 {showLeaderboard ? 'Hide' : 'View'} Leaderboard
                </button>
                {showLeaderboard && (
                  <div className="mt-3 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-3 border border-blue-100">
                    <h4 className="font-semibold mb-2 text-xs sm:text-sm">🏆 Top 5 Scores</h4>
                    <div className="space-y-2">
                      {leaderboard.slice(0, 5).map((entry, index) => (
                        <div key={index} className="flex justify-between items-center text-xs bg-white rounded px-3 py-2">
                          <span className="font-bold text-gray-700">#{index + 1}</span>
                          <span className="font-semibold text-blue-600">{entry.similarityScore}%</span>
                          <span className="text-gray-500">{formatTime(entry.completionTime)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* DESKTOP: 3-column layout */}
          <div className="hidden lg:grid grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Reference Image</h3>
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border-2 border-gray-200">
                <img src={currentPlayer.image} alt={currentPlayer.name} className="w-full h-full object-contain pointer-events-none" />
              </div>
              <button onClick={() => setShowLeaderboard(!showLeaderboard)} className="w-full mt-4 bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium">
                {showLeaderboard ? '🔽 Hide' : '🏆 View'} Leaderboard
              </button>
              {showLeaderboard && leaderboard.length > 0 && (
                <div className="mt-4 bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-4 border border-blue-100">
                  <h4 className="font-semibold mb-3 text-sm">🏆 Top 5</h4>
                  <div className="space-y-2">
                    {leaderboard.slice(0, 5).map((entry, index) => (
                      <div key={index} className="flex justify-between text-xs bg-white rounded px-3 py-2">
                        <span className="font-bold">#{index + 1}</span>
                        <span className="font-semibold text-blue-600">{entry.similarityScore}%</span>
                        <span className="text-gray-500">{formatTime(entry.completionTime)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Your Drawing</h3>
                <button onClick={clearCanvas} aria-label="Clear drawing" className="text-gray-500 hover:text-gray-700 px-3 py-1.5 text-sm border rounded-lg">
                  🗑️ Clear
                </button>
              </div>
              <div className="relative flex w-full items-center justify-center border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-white canvas-container">
                <canvas ref={desktopCanvasRef} className="drawing-canvas block w-full h-auto cursor-crosshair touch-none select-none" style={{ touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none' }} onPointerDown={startDrawing} onPointerMove={draw} onPointerUp={stopDrawing} onPointerCancel={stopDrawing} onLostPointerCapture={stopDrawing} />
              </div>
              <button onClick={submitDrawing} disabled={loading || showResult} className="w-full mt-4 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:text-slate-500 disabled:cursor-not-allowed text-white px-4 py-3 rounded-lg text-base font-semibold">
                {loading ? <span className="flex items-center justify-center gap-2"><svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>Analyzing...</span> : showResult ? '✓ Drawing Submitted' : '✨ Submit Drawing'}
              </button>
              <div className={`min-h-[20px] mt-2 text-center text-xs font-medium ${drawingError ? 'text-red-600' : 'text-gray-500'}`} role="status" aria-live="polite">
                {drawingError || (!hasDrawing ? 'Draw on the canvas before submitting.' : 'Your drawing is ready to submit.')}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-semibold mb-4">Result</h3>
              {!showResult ? (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" /></svg>
                  <p className="text-sm">Complete and submit to see score!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="text-center bg-gradient-to-br from-blue-50 to-purple-50 rounded-lg p-6">
                    <div className="text-5xl font-bold text-blue-600 mb-2">{result.score}%</div>
                    <p className="text-gray-600 font-medium">Similarity Score</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Rank</div>
                      <div className="text-xl text-blue-600 font-bold">#{result.rank}</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                      <div className="text-sm font-semibold text-gray-700 mb-1">Time</div>
                      <div className="text-xl text-green-600 font-bold">{formatTime(result.completionTime)}</div>
                    </div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold mb-3 text-gray-700">Score Categories</h4>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span>Draw (90-100%)</span>
                        <span className={result.score >= 90 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Good (70-89%)</span>
                        <span className={result.score >= 70 && result.score < 90 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Average (50-69%)</span>
                        <span className={result.score >= 50 && result.score < 70 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Poor (0-49%)</span>
                        <span className={result.score < 50 ? 'text-blue-600 font-bold' : 'text-gray-400'}>●</span>
                      </div>
                    </div>
                  </div>
                  <button onClick={() => { setGameStartTime(Date.now()); clearCanvas(); }} className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-3 rounded-lg font-semibold">
                    🔄 Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-3 sm:py-6">
          <div className="flex flex-col gap-2 sm:gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
              <Link
                to="/"
                className="inline-flex w-fit items-center border border-blue-500 text-blue-600 hover:bg-blue-50 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 sm:px-4 py-1.5 sm:py-2 rounded transition-all self-start"
              >
                <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-1.5 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="sm:hidden">Back</span>
                <span className="hidden sm:inline">Back to Home</span>
              </Link>

              <div className="flex-1 min-w-0 px-0 sm:px-2">
                <div className="flex items-center justify-center gap-3 min-w-0">
                  <div className="hidden sm:block text-4xl md:text-5xl flex-shrink-0 leading-none">⚽✏️</div>
                  <h1 className="text-base sm:text-2xl md:text-3xl font-bold text-gray-900 leading-none whitespace-nowrap tracking-tight text-center">
                    DRAW THE LEGEND
                  </h1>
                </div>
              </div>

              <div className="hidden sm:block w-16 sm:w-24"></div>
            </div>
            <p className="text-gray-600 text-[11px] sm:text-sm md:text-base text-center">
              Choose a sports legend and sketch their portrait
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">🏆 Sport</label>
              <select value={selectedSport} onChange={(e) => setSelectedSport(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-blue-500">
                {sports.map(sport => <option key={sport.value} value={sport.value}>{sport.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">⚡ Difficulty</label>
              <select value={selectedDifficulty} onChange={(e) => setSelectedDifficulty(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm sm:text-base focus:ring-2 focus:ring-blue-500">
                {difficulties.map(difficulty => <option key={difficulty.value} value={difficulty.value}>{difficulty.label}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* Players Grid - PERFECTED DESIGN */}
        {loading ? (
          <div className="text-center py-12 sm:py-16">
            <div className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600 text-sm">Loading players...</p>
          </div>
        ) : players.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <div className="text-5xl mb-4">🔍</div>
            <p className="text-gray-600">No players found. Try adjusting filters.</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 sm:gap-4 lg:gap-5">
            {players.map(player => (
              <div
                key={player._id}
                className="player-card"
                onClick={() => activatePlayerCard(player)}
                onKeyDown={(event) => handlePlayerCardKeyDown(event, player)}
                role="button"
                tabIndex={0}
                aria-label={`Draw ${player.name}`}
              >
                {/* Compact Image Container - 40% height */}
                <div className="player-card-image">
                  <div className="player-card-image-inner">
                    <img
                      src={player.image}
                      alt={player.name}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.nextSibling.style.display = 'flex';
                      }}
                    />
                  </div>
                  <div className="player-card-image-fallback" style={{display: 'none'}}>
                    <svg className="w-8 h-8 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                </div>
                
                {/* Content Container - 60% height */}
                <div className="player-card-content">
                  {/* Name & Badge Row */}
                  <div className="player-card-header">
                    <h3 className="player-card-title">{player.name}</h3>
                    <span className={`player-card-badge ${getDifficultyColor(player.difficulty)}`}>
                      {player.difficulty === 'easy' ? 'E' : player.difficulty === 'medium' ? 'M' : 'H'}
                    </span>
                  </div>
                  
                  {/* Sport */}
                  <p className="player-card-sport">{player.sport}</p>
                  
                  {/* Team/Position */}
                  <div className="player-card-team">
                    {player.team && (
                      <p className="line-clamp-1">
                        {player.team}{player.position && ` • ${player.position}`}
                      </p>
                    )}
                  </div>
                  
                  {/* Button */}
                  <div className="player-card-button" aria-hidden="true">
                    🎨 Draw
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Enhanced Styles - PERFECT CARD DESIGN */}
      <style>{`
        /* Player Card - Uniform Design */
        .player-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          overflow: hidden;
          display: flex;
          flex-direction: column;
          height: 320px; /* Fixed height for uniformity */
          transition: all 0.3s ease;
          cursor: pointer;
        }

        .player-card:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
          transform: translateY(-4px);
        }

        .player-card:focus-visible {
          outline: 3px solid #60a5fa;
          outline-offset: 3px;
        }

        /* Larger image area so the full player photo stays visible */
        .player-card-image {
          height: 52%;
          min-height: 160px;
          max-height: 160px;
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          flex-shrink: 0;
          padding: 6px;
        }

        .player-card-image-inner {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .player-card-image img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }

        .player-card-image-fallback {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f3f4f6;
        }

        @media (min-width: 640px) {
          .player-card {
            height: 360px;
          }

          .player-card-image {
            min-height: 190px;
            max-height: 190px;
            padding: 8px;
          }
        }

        @media (min-width: 1024px) {
          .player-card {
            height: 400px;
          }

          .player-card-image {
            min-height: 220px;
            max-height: 220px;
            padding: 10px;
          }
        }

        /* Content - 60% of card height */
        .player-card-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          padding: 12px;
          min-height: 0;
        }

        /* Header with name and badge */
        .player-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 6px;
          margin-bottom: 6px;
          min-height: 40px;
        }

        .player-card-title {
          flex: 1;
          font-size: 13px;
          font-weight: 600;
          color: #1f2937;
          line-height: 1.3;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          word-break: break-word;
        }

        .player-card-badge {
          flex-shrink: 0;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 700;
          padding: 0;
        }

        .player-card-sport {
          font-size: 11px;
          color: #6b7280;
          font-weight: 500;
          margin-bottom: 4px;
          height: 16px;
          line-height: 16px;
        }

        .player-card-team {
          font-size: 10px;
          color: #9ca3af;
          margin-bottom: 8px;
          height: 14px;
          line-height: 14px;
          min-height: 14px;
        }

        .player-card-button {
          width: 100%;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          border-radius: 8px;
          padding: 8px 12px;
          font-size: 12px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: auto;
          box-shadow: 0 2px 4px rgba(59, 130, 246, 0.2);
        }

        .player-card-button:hover {
          background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
          box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
          transform: translateY(-1px);
        }

        .player-card-button:active {
          transform: translateY(0);
        }

        /* Responsive adjustments */
        @media (min-width: 640px) {
          .player-card {
            height: 300px;
          }
          .player-card-image {
            max-height: 120px;
          }
          .player-card-title {
            font-size: 14px;
          }
          .player-card-badge {
            width: 22px;
            height: 22px;
            font-size: 11px;
          }
          .player-card-sport {
            font-size: 12px;
          }
          .player-card-button {
            font-size: 13px;
            padding: 9px 14px;
          }
        }

        @media (min-width: 1024px) {
          .player-card {
            height: 320px;
          }
          .player-card-image {
            max-height: 128px;
          }
          .player-card-content {
            padding: 14px;
          }
          .player-card-title {
            font-size: 15px;
          }
          .player-card-badge {
            width: 24px;
            height: 24px;
            font-size: 11px;
            border-radius: 5px;
          }
          .player-card-button {
            font-size: 14px;
            padding: 10px 16px;
          }
        }

        /* Canvas styles */
        .canvas-container {
          position: relative;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
        }

        .drawing-canvas {
          display: block;
          width: 100%;
          height: auto;
          cursor: crosshair;
          touch-action: none;
          user-select: none;
          -webkit-user-select: none;
        }

        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        body {
          overflow-x: hidden;
          overscroll-behavior-y: contain;
        }

        * {
          -webkit-tap-highlight-color: rgba(59, 130, 246, 0.2);
        }
      `}</style>
    </div>
  );
};

export default DrawGame;
